'use client'

import { useAuthorization } from '@entities/authentication'
import { paymentEntityMutations } from '@entities/payment'
import { SubscribeDiscountBadge, SubscribeOriginalPrice, useSubscribeErrorStaticModel } from '@entities/subscribes'
import { userEntityQueries } from '@entities/users'
import { BaseResponseSchema, CurriculumType, destinationSchema, LangType, PaymentType } from '@server/schemas'
import { useI18n } from '@shared/config/i18n'
import { UI_IDS } from '@shared/constants'
import { openConfirmDialogV2 } from '@shared/ui'
import { formatKrwAmount } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import React, { ComponentProps, ReactNode } from 'react'

import { Button, ButtonV1, Flex, Icon, Typography, VStack } from '@podo-app/design-system-temp'

const INVALID_PHONE_NUMBER = '00000000000' // 000-0000-0000
const isValidPhoneNumber = (phoneNumber?: string): boolean => {
  if (!phoneNumber) {
    return false
  }
  return phoneNumber !== INVALID_PHONE_NUMBER
}

type Props = {
  price: number
  title: string
  descriptions: ReactNode[]
  subscribeTicketId: string
  curriculumType: CurriculumType
  paymentType: PaymentType
  langType: Exclude<LangType, 'ENJP'>
}

const SmartTalkTrialTicketCard = ({
  price,
  title,
  descriptions,
  subscribeTicketId: subscribeId,
  curriculumType,
  paymentType,
  langType,
}: Props) => {
  const router = useRouter()
  const { effectiveLocale, t } = useI18n()

  const { bearerToken } = useAuthorization()
  const { data: user } = useQuery({
    ...userEntityQueries.getCurrentUser({ bearerToken }),
    enabled: Boolean(bearerToken),
  })
  const { SUBSCRIBE_ERROR_MAP } = useSubscribeErrorStaticModel()

  const validatePayment = useMutation({
    mutationFn: async () => {
      if (!user) {
        throw new Error('NOT_AUTHORIZED')
      }
      if (!isValidPhoneNumber(user.phone)) {
        throw new Error('INVALID_PHONE_NUMBER')
      }

      const { mutationFn } = paymentEntityMutations.validatePayment({
        subscribeId,
        bearerToken,
      })

      return await mutationFn()
    },
    onSuccess: () => {
      router.push(`/subscribes/trial/smart-talk/${subscribeId}`)
    },
    onError: (error) => {
      if (error instanceof Error && error.message === 'NOT_AUTHORIZED') {
        const destination = destinationSchema.Enum.SMART_TALK_TRIAL_TICKET_LIST
        return openConfirmDialogV2({
          ConfirmHeader: () => <ConfirmHeader>{SUBSCRIBE_ERROR_MAP.NOT_AUTHORIZED}</ConfirmHeader>,
          ConfirmFooter: ({ handleClose }) => {
            const handleConfirm = () => {
              router.push(`/login?destination=${destination}`)
              handleClose()
            }
            return (
              <Button size="xLarge" className="w-full" onClick={handleConfirm}>
                {t('common.auth.login')}
              </Button>
            )
          },
        })
      }
      if (error instanceof Error && error.message === 'INVALID_PHONE_NUMBER') {
        return openConfirmDialogV2({
          ConfirmHeader: () => <ConfirmHeader>{SUBSCRIBE_ERROR_MAP.MUST_REGISTER_PHONE_NUMBER}</ConfirmHeader>,
          ConfirmFooter: () => {
            const handleConfirm = () => {
              router.push('/my-podo')
            }
            return (
              <Button size="xLarge" className="w-full" onClick={handleConfirm}>
                {t('common.actions.confirm')}
              </Button>
            )
          },
        })
      }
      if (error instanceof Error) {
        const cause = error.cause as BaseResponseSchema<
          null,
          'DUPLICATE_LESSON' | 'DELINQUENT_PAYMENT' | 'HAS_TICKET_HISTORY' | 'HAS_ACTIVE_TICKET'
        >
        const hasTicketHistory = cause.resultCdName === 'HAS_TICKET_HISTORY'

        return openConfirmDialogV2({
          ConfirmHeader: () => <ConfirmHeader>{SUBSCRIBE_ERROR_MAP[cause.resultCdName]}</ConfirmHeader>,
          ConfirmFooter: ({ handleClose }) => {
            const handleConfirm = () => {
              if (hasTicketHistory) {
                router.push('/subscribes/tickets/smart-talk')
              }
              handleClose()
            }
            return (
              <React.Fragment>
                {hasTicketHistory ? (
                  <ButtonV1 variant="ghost" size="xLarge" className="!w-[98px]" onClick={handleClose}>
                    {t('common.actions.cancel')}
                  </ButtonV1>
                ) : null}
                <Button size="xLarge" className="w-full" onClick={handleConfirm}>
                  {hasTicketHistory ? t('subscribeList.ticket.viewLessons') : t('common.actions.confirm')}
                </Button>
              </React.Fragment>
            )
          },
        })
      }
    },
  })

  const handleClickPurchaseButton = () => {
    validatePayment.mutate()
  }

  return (
    <VStack className="p-5 rounded-xl border-gray-200 border gap-5" data-testid={`item-trial-ticket-${subscribeId}`}>
      <VStack className="gap-2">
        <Flex className="justify-between">
          <Typography size="h3">{title}</Typography>
        </Flex>

        <SubscribeOriginalPrice
          curriculumType={curriculumType}
          paymentType={paymentType}
          langType={langType}
          perMonth={false}
        />

        <Flex className="items-center gap-0.5">
          <SubscribeDiscountBadge curriculumType={curriculumType} paymentType={paymentType} langType={langType} />

          <Typography size="h1" className="!text-[22px]">
            {formatKrwAmount(price, effectiveLocale)}
          </Typography>
        </Flex>

        <VStack>
          {descriptions.map((description, index) => (
            <Flex key={index} className="items-center gap-1.5">
              <LineCheckIcon />
              <Typography size="body1">{description}</Typography>
            </Flex>
          ))}
        </VStack>
      </VStack>

      <Button
        className="w-full"
        size="large"
        onClick={handleClickPurchaseButton}
        disabled={validatePayment.isPending}
        data-testid={`btn-purchase-trial-${subscribeId}`}
        eventParams={{ name: UI_IDS.btn.purchaseTrial(subscribeId), location: 'subscribes', action: 'submit' }}
      >
        {t('subscribeList.ticket.purchase')}
      </Button>
    </VStack>
  )
}

const LineCheckIcon = (props: ComponentProps<typeof Icon>) => (
  <Icon width={16} height={16} viewBox="0 0 16 16" {...props}>
    <path
      d="M4 8.5L6.47487 10.9749L12.1317 5.31802"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Icon>
)

export default SmartTalkTrialTicketCard

const ConfirmHeader = ({ children }: { children?: ReactNode }) => {
  return (
    <VStack className="gap-2 items-center">
      <Typography size="h3" className="flex text-center flex-1 text-gray-900 min-h-12 items-center font-medium">
        {children}
      </Typography>
    </VStack>
  )
}
